import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRealtime } from "@tcg/realtime/react";
import { PauseCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { usePos } from "@/app/context";
import { BigButton } from "@/components/BigButton";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useCart } from "@/features/sale/hooks/useCart";
import { call } from "@/lib/api";
import { errorText, isApiError } from "@/lib/errors";
import { POS_TOPICS } from "@/lib/sse";
import type { Hold } from "@/lib/types";

import { HoldCard } from "./HoldCard";
import { HoldModal } from "./HoldModal";

export const holdsKey = ["pos-holds", "list", "screen"] as const;

const TICK_MS = 15_000;

/**
 * 保留一覧 (T07-28): every 保留 / 取り置き of this register as a card (who, items, total, time
 * left in colour). 「呼び出す」 opens the basket again as it was parked (the server warns when a
 * price changed meanwhile); 「取消」 gives the cards back to the shop (the manager when a deposit
 * was taken). The list follows the register's stream (`hold.changed` — the minute job that ends
 * a 保留 whose time is up) and its own clock, so 「期限切れ」 shows without a refresh.
 */
export function HoldsScreen() {
  const { t } = useTranslation();
  const { api } = usePos();
  const cart = useCart();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { withApproval } = useApproval();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState<string | null>(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [cancelling, setCancelling] = useState<Hold | null>(null);
  const [reason, setReason] = useState("");

  const holds = useQuery({
    queryKey: holdsKey,
    queryFn: () => call(() => api.GET("/v1/pos/holds")),
  });
  useRealtime([POS_TOPICS.hold], () => void holds.refetch(), {
    onResync: () => void holds.refetch(),
  });
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["pos-holds"] });
  };

  const resume = async (hold: Hold) => {
    setBusy(hold.id);
    try {
      const resumed = await call(() =>
        api.POST("/v1/pos/holds/{hold_id}/resume", { params: { path: { hold_id: hold.id } } }),
      );
      if (resumed.cartId) {
        await cart.openCart(resumed.cartId);
      }
      await refresh();
      const changed = (resumed.warnings ?? []).length;
      toast.show(
        changed > 0 ? t("holds.resumed_price_changed", { count: changed }) : t("holds.resumed"),
        changed > 0 ? "info" : "success",
      );
      void navigate("/");
    } catch (err) {
      toast.show(
        isApiError(err, "cart_not_empty") ? t("holds.cart_not_empty") : errorText(err, t),
        "error",
      );
      void refresh();
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (hold: Hold) => {
    setBusy(hold.id);
    try {
      await withApproval(
        (token) =>
          call(() =>
            api.POST("/v1/pos/holds/{hold_id}/cancel", {
              params: { path: { hold_id: hold.id } },
              body: reason.trim() ? { reason: reason.trim() } : {},
              headers: token ? { "X-Approval-Token": token } : {},
            }),
          ),
        { subject: hold.id, amount: hold.deposit?.amount ?? null },
      );
      setCancelling(null);
      setReason("");
      toast.show(hold.deposit ? t("holds.cancelled_deposit") : t("holds.cancelled"), "success");
      await refresh();
    } catch (err) {
      toast.show(
        err instanceof ApprovalCancelled ? t("approval.cancelled") : errorText(err, t),
        "error",
      );
    } finally {
      setBusy(null);
    }
  };

  const rows = holds.data?.data ?? [];
  const basketHasItems = (cart.cart?.lines.length ?? 0) > 0;
  return (
    <section aria-labelledby="holds-title" className="flex h-full min-h-0 flex-col gap-3 p-4">
      <header className="flex items-center gap-3">
        <h1 id="holds-title" className="flex-1 text-2xl font-semibold">
          {t("holds.title")}
        </h1>
        <BigButton
          variant="outline"
          onClick={() => setHoldOpen(true)}
          disabled={!basketHasItems}
          hint={basketHasItems ? undefined : t("holds.nothing_to_hold")}
        >
          <PauseCircle aria-hidden />
          {t("holds.hold_current")}
        </BigButton>
      </header>
      {rows.length === 0 && !holds.isLoading ? (
        <p className="text-lg text-muted-foreground">{t("holds.empty")}</p>
      ) : null}
      <ul className="grid min-h-0 grid-cols-2 content-start gap-3 overflow-y-auto xl:grid-cols-3">
        {rows.map((hold) => (
          <HoldCard
            key={hold.id}
            hold={hold}
            now={now}
            busy={busy !== null}
            onResume={() => void resume(hold)}
            onCancel={() => setCancelling(hold)}
          />
        ))}
      </ul>
      <HoldModal
        open={holdOpen}
        onClose={() => setHoldOpen(false)}
        onHeld={() => {
          setHoldOpen(false);
          void refresh();
        }}
      />
      {cancelling ? (
        <Modal
          open
          title={t("holds.cancel_title")}
          description={cancelling.deposit ? t("holds.cancel_deposit_help") : t("holds.cancel_help")}
          onClose={() => setCancelling(null)}
          busy={busy !== null}
          footer={
            <>
              <button
                type="button"
                onClick={() => setCancelling(null)}
                className="min-h-pos-touch rounded-md border px-6 text-lg"
              >
                {t("app.back")}
              </button>
              <button
                type="button"
                onClick={() => void cancel(cancelling)}
                disabled={busy !== null}
                className="min-h-pos-touch rounded-md bg-status-problem px-6 text-lg font-semibold text-white"
              >
                {t("holds.cancel_confirm")}
              </button>
            </>
          }
        >
          <label className="flex flex-col gap-1">
            <span>{t("holds.cancel_reason")}</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              className="min-h-pos-touch rounded-md border px-3 text-lg"
            />
          </label>
        </Modal>
      ) : null}
    </section>
  );
}
